import { sendEmailVerification, reload } from 'firebase/auth';
import { firebaseAuth } from '@mp/utils';
import { useNavigate } from 'react-router-dom';
import { useProNavigate } from '@mp/contexts';
import { useQueryParams, JsonParam, StringParam } from 'use-query-params';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, Text, VStack, Label, HStack } from '@medely/ui-kit';
import { useCurrentUser } from '@mp/hooks';
import { PageLayout } from '../../components/pageLayout/PageLayout.web';
import { FixedFooterLayout } from '../../components/FixedFooterLayout/FixedFooterLayout.web';
import { MfaStepItem, MfaErrorAlert } from './components';
import { maskEmail } from './utils/maskEmail';
import { useApplicationMutations } from '../application/applicationFlowMutations';
import { MFAFlowType } from './utils/types';

export const MfaIdentityVerificationScreen = () => {
  const routerNavigate = useNavigate();
  const { navigate } = useProNavigate();
  const [queryParams] = useQueryParams({
    forward: JsonParam,
    flow: StringParam,
  });
  const flowType = (queryParams.flow as MFAFlowType) || 'SENSITIVE_SCREEN';
  const { prevStepWithInvalidate, isLoading } = useApplicationMutations();

  const { currentUser } = useCurrentUser();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInteracted = useRef(false);
  const [canResend, setCanResend] = useState(false);
  const [mustVerifyEmail, setMustVerifyEmail] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(firebaseAuth.currentUser);

  firebaseAuth.onAuthStateChanged((user) => {
    setFirebaseUser(user);
  });

  const sendVerificationEmail = async () => {
    const firebaseUser = firebaseAuth.currentUser;
    if (firebaseUser && !firebaseUser.emailVerified) {
      await sendEmailVerification(firebaseUser);
    }
  };

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (!hasInteracted.current) {
        setCanResend(true);
      }
    }, 30000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    (async () => {
      sendVerificationEmail();
    })();
  }, []);

  const resendEmailConfirmation = () => {
    setCanResend(false);
    timerRef.current = setTimeout(() => setCanResend(true), 30_000);
    sendVerificationEmail();
  };

  const onHandleContinue = async () => {
    const firebaseUser = firebaseAuth.currentUser;
    if (firebaseUser) {
      await reload(firebaseUser);
      const updatedFirebaseUser = firebaseAuth.currentUser;
      if (!updatedFirebaseUser?.emailVerified) {
        setMustVerifyEmail(true);
        return;
      }
    }

    navigate(
      'MfaSmsCodeEntryScreen' as any,
      {
        forward: JSON.stringify(queryParams.forward),
        flow: flowType,
      } as any,
    );
  };

  return (
    <PageLayout handleBack={() => routerNavigate(-1)}>
      <FixedFooterLayout
        height="90%"
        footerContent={
          <HStack px={4} gap={2}>
            {flowType === 'SIGN_UP' &&
              currentUser?.professional?.application_status === 'markets' && (
                <Button
                  loading={isLoading}
                  variant="outlined"
                  fullWidth
                  onClick={() => prevStepWithInvalidate()}
                >
                  Back
                </Button>
              )}
            <Button
              loading={isLoading}
              disabled={!firebaseUser}
              variant="contained"
              color="primary"
              fullWidth
              onClick={onHandleContinue}
            >
              Continue
            </Button>
          </HStack>
        }
      >
        <VStack p={4} gap={6}>
          <Box>
            <Heading size="s" color="text.primary">
              Identity verification
            </Heading>
            <Text size="m" color="text.secondary">
              We&apos;ve added additional security protections to make sure your account details
              stay safe!
            </Text>
            {canResend && (
              <Box pl={2} mt={2} onClick={resendEmailConfirmation}>
                <Label size="m" color="state.teal.secondary">
                  Resend email verification
                </Label>
              </Box>
            )}
          </Box>

          <VStack gap={2}>
            <MfaStepItem
              iconName="award"
              title="Email verification"
              description={`Please click the link we sent you at ${maskEmail(
                currentUser?.email,
              )}. You'll only need to verify your email once.`}
            />
            <MfaStepItem
              iconName="phone"
              title="Phone number"
              description="Your phone number will be used to send a verification code."
            />
          </VStack>

          <MfaErrorAlert
            visible={mustVerifyEmail}
            message="You must first click on the link in the email to continue."
          />
        </VStack>
      </FixedFooterLayout>
    </PageLayout>
  );
};
